import React, { useState } from "react"
import { Link, graphql } from "gatsby"
import styled from "styled-components"
import { MDXRenderer } from "gatsby-plugin-mdx"
import { MDXProvider } from "@mdx-js/react"

import Layout from "../components/layout"
import { CategorySEO } from "../components/seo"
import Articles from "../components/articles"
import Pagination from "../components/pagination"
import Subcategories from "../components/subcategories"
import { getCategoryUrl } from "../utilities"
import { sLayout } from "../styled/layout"

const Header = styled.div`
  button {
    margin-top: 35px;

    @media only screen and (min-width: ${props => props.theme.mq.small}) {
      margin-top: 50px;
    }
  }
`

const Label = styled(Link)`
  color: ${props => props.theme.colors.lightgrey};
  display: block;
  font-size: 13px;
  line-height: 1.6;
  margin-bottom: 5px;
  transition: color 0.3s;

  @media only screen and (min-width: ${props => props.theme.mq.small}) {
    font-size: 14px;
    margin-bottom: 8px;
  }

  .no-touchevents &:hover {
    color: ${props => props.theme.colors.primary};
  }
`

const H1 = styled.h1`
  font-size: 31px;
  font-weight: ${props => props.theme.title.w};
  line-height: 1.2;
  margin-bottom: 20px;

  @media only screen and (min-width: ${props => props.theme.mq.small}) {
    font-size: 48px;
    margin-bottom: 35px;
  }
`

const Text = styled.div`
  font-size: ${props => props.theme.text.sm};
  font-style: italic;
  font-weight: ${props => props.theme.text.w};
  line-height: ${props => props.theme.text.lhm};

  @media only screen and (min-width: ${props => props.theme.mq.small}) {
    font-size: ${props => props.theme.text.sd};
    line-height: ${props => props.theme.text.lhd};
  }

  p {
    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }

    &:not(:first-child) {
      display: ${props => (props.expanded ? `block` : `none`)};
    }

    a,
    button {
      color: ${props => props.theme.colors.primary};
      text-decoration: underline;

      .no-touchevents &:hover {
        text-decoration: none;
      }
    }

    button {
      font-family: inherit;
      font-size: inherit;
      font-weight: ${props => props.theme.title.w};
      margin-top: 0;
      margin-left: 5px;
    }
  }

  strong,
  b {
    font-weight: ${props => props.theme.title.w};
  }
`

const Section = styled.section`
  margin-top: 45px;

  @media only screen and (min-width: ${props => props.theme.mq.small}) {
    margin-top: 60px;
  }
`

const CategoryTemplate = ({ data: { category, posts }, pageContext: { numPages, currentPage, lang } }) => {
  const [openSubcategories, setOpenSubcategories] = useState(false)
  const [textExpanded, setTextExpanded] = useState(false)

  const toggleSubcategories = () => {
    setOpenSubcategories(prevOpenSubcategories => !prevOpenSubcategories)
  }

  const toggleTextExpanded = () => {
    setTextExpanded(prevTextExpanded => !prevTextExpanded)
  }

  const MoreButton = () =>
    !textExpanded && (
      <button type="button" onClick={toggleTextExpanded}>
        {lang === `it` ? `Continua a leggere` : `Read more`}
      </button>
    )

  const LessButton = () =>
    textExpanded && (
      <button type="button" onClick={toggleTextExpanded}>
        {lang === `it` ? `Riduci` : `Read less`}
      </button>
    )

  const shortcodes = {
    ccmore: MoreButton,
    ccless: LessButton,
  }

  return (
    <Layout>
      <CategorySEO
        title={`${category.metaTitle || category.name}${currentPage > 1 ? ` - Pagina ${currentPage}` : ``}`}
        category={category}
        posts={posts.edges.map(post => post.node)}
        page={currentPage}
        pageCount={numPages}
      />
      <Header>
        {category.parentCategory ? (
          <Label to={getCategoryUrl(category.parentCategory.slug)}>{category.parentCategory.name}</Label>
        ) : null}
        <H1>{category.name}</H1>
        <Text expanded={textExpanded}>
          <MDXProvider components={shortcodes}>
            <MDXRenderer>{category.fields.description.childMdx.body}</MDXRenderer>
          </MDXProvider>
        </Text>
        {category.subcategories ? (
          <sLayout.Button as="button" className="icon-plus" onClick={toggleSubcategories}>
            Paesi
          </sLayout.Button>
        ) : null}
      </Header>
      <Section>
        <Articles posts={posts.edges.map(post => post.node)} hideCategory={!category.subcategories} />
        <Pagination baseUrl={getCategoryUrl(category.slug)} pageCount={numPages} initialPage={currentPage} />
      </Section>
      <Subcategories
        title={category.name}
        subcategories={category.subcategories}
        toggleSubcategories={toggleSubcategories}
        open={openSubcategories}
      />
    </Layout>
  )
}

export default CategoryTemplate

export const pageQuery = graphql`
  query CategoryBySlug($slug: String!, $id: String!, $skip: Int!, $limit: Int!) {
    category: crossCastMagCategory(slug: { eq: $slug }) {
      parentCategory {
        slug
        name
        parentCategory {
          slug
          name
          parentCategory {
            slug
            name
          }
        }
      }
      slug
      name
      metaTitle
      abstract
      fields {
        description {
          childMdx {
            body
          }
        }
      }
      subcategories {
        slug
        name
      }
      launchImage {
        guid
        platformSlug
        alt
        title
        credits
        caption
        width
        height
        ext
      }
    }
    posts: allBlogPost(
      filter: { categories: { elemMatch: { id: { eq: $id } } } }
      sort: { fields: [publishedAt], order: DESC }
      limit: $limit
      skip: $skip
    ) {
      edges {
        node {
          slug
          title
          abstract
          featuredImage {
            guid
            platformSlug
            type
            alt
            width
            height
            ext
          }
          category {
            name
          }
        }
      }
    }
  }
`
